import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PaymentsService } from '../../services/payments.service';

type PendingRun = {
	journalno: string | number;
	created_by?: string | null;
	created_at?: string | null;
	dateran?: string | null;
	totalAmount: number;
	currency?: string;
	vendorCount: number;
	vendorNames?: string[];
	lineCount?: number;
};

type SortMode = 'newest' | 'oldest' | 'largest' | 'smallest';

@Component({
	selector: 'app-pending-payment-runs',
	templateUrl: './pending-payment-runs.component.html',
	styleUrls: ['./pending-payment-runs.component.scss']
})
export class PendingPaymentRunsComponent implements OnInit {
	runs: PendingRun[] = [];
	loading = true;
	color = 'blue';
	filterText = '';
	sortMode: SortMode = 'newest';
	discardingJournal: string | number | null = null;

	constructor(
		private router: Router,
		private globalSearchService: GlobalSearchService,
		private paymentsService: PaymentsService
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.loadRuns();
	}

	loadRuns(): void {
		this.loading = true;
		this.paymentsService.getPendingRuns().subscribe(
			(res: any) => {
				this.loading = false;
				this.runs = res?.success && Array.isArray(res.runs) ? res.runs : [];
			},
			() => {
				this.loading = false;
				this.runs = [];
				this.globalSearchService.showNotification(
					'Could not load pending runs — check your connection and try again.',
					'error', 'bottom', 'right'
				);
			}
		);
	}

	/** Filtered + sorted view of runs (drives the template). */
	get filteredRuns(): PendingRun[] {
		const q = this.filterText.trim().toLowerCase();
		let list = this.runs;
		if (q) {
			list = list.filter(r => {
				if (String(r.journalno).toLowerCase().includes(q)) return true;
				if ((r.created_by || '').toLowerCase().includes(q)) return true;
				const names = (r.vendorNames || []).join(' ').toLowerCase();
				return names.includes(q);
			});
		}
		return [...list].sort((a, b) => {
			switch (this.sortMode) {
				case 'oldest':   return this.tsOf(a) - this.tsOf(b);
				case 'largest':  return (b.totalAmount || 0) - (a.totalAmount || 0);
				case 'smallest': return (a.totalAmount || 0) - (b.totalAmount || 0);
				default:         return this.tsOf(b) - this.tsOf(a);
			}
		});
	}

	get summaryTotal(): number {
		return this.runs.reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);
	}

	get summaryLines(): number {
		return this.runs.reduce((sum, r) => sum + (Number(r.lineCount) || 0), 0);
	}

	private tsOf(r: PendingRun): number {
		const s = r.created_at || r.dateran || '';
		if (!s) return 0;
		const d = new Date(s);
		return isNaN(d.getTime()) ? 0 : d.getTime();
	}

	/** "3 hours ago" / "yesterday" / "Jun 10, 2026". */
	relativeTime(s: string | null | undefined): string {
		if (!s) return '';
		const d = new Date(s);
		if (isNaN(d.getTime())) return s;
		const now = Date.now();
		const diffMs = now - d.getTime();
		const min = Math.round(diffMs / 60000);
		if (min < 1) return 'Just now';
		if (min < 60) return `${min} min ago`;
		const hr = Math.round(min / 60);
		if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
		const day = Math.round(hr / 24);
		if (day === 1) return 'Yesterday';
		if (day < 7) return `${day} days ago`;
		return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
	}

	/** Stale = older than 3 days; the clerk likely forgot about it. */
	isStale(run: PendingRun): boolean {
		const ts = this.tsOf(run);
		if (!ts) return false;
		return (Date.now() - ts) > (3 * 24 * 60 * 60 * 1000);
	}

	/** Initials from "First Last" / username / id — used for the avatar bubble. */
	initialsOf(s: string | null | undefined): string {
		if (!s) return '?';
		const cleaned = String(s).trim();
		if (!cleaned) return '?';
		const parts = cleaned.split(/[\s_.@-]+/).filter(Boolean);
		if (parts.length >= 2) {
			return (parts[0][0] + parts[1][0]).toUpperCase();
		}
		return cleaned.substring(0, 2).toUpperCase();
	}

	clearFilter(): void {
		this.filterText = '';
	}

	openRun(run: PendingRun): void {
		this.router.navigate(['/vendors-check-print', run.journalno]);
	}

	discardRun(run: PendingRun, event: Event): void {
		event.stopPropagation();
		if (this.discardingJournal != null) return;
		if (!confirm(`Discard payment run #${run.journalno}?\n\nNothing has been posted; the pending run entry will be removed.`)) {
			return;
		}
		this.discardingJournal = run.journalno;
		this.paymentsService.discardRun({ journal: run.journalno }).subscribe(
			(res: any) => {
				this.discardingJournal = null;
				if (res?.success) {
					this.globalSearchService.showNotification(`Run #${run.journalno} discarded.`, 'success', 'bottom', 'right');
					this.runs = this.runs.filter(r => r.journalno !== run.journalno);
				} else {
					this.globalSearchService.showNotification(
						res?.error || 'Failed to discard the run — it may have already been posted.',
						'error', 'bottom', 'right'
					);
				}
			},
			(err: any) => {
				this.discardingJournal = null;
				this.globalSearchService.showNotification(
					err?.error?.error || 'Failed to discard the run.',
					'error', 'bottom', 'right'
				);
			}
		);
	}

	formatDate(val: string | null | undefined): string {
		if (!val) return '';
		const d = new Date(val);
		return isNaN(d.getTime()) ? val : d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}

	formatMoney(n: number, currency = 'USD'): string {
		try {
			return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(n || 0);
		} catch {
			return '$' + (n || 0).toFixed(2);
		}
	}

	back(): void {
		this.router.navigate(['/vendors-check-print']);
	}

	newRun(): void {
		this.router.navigate(['/vendors-check-print']);
	}

	trackByJournal(_: number, r: PendingRun): string | number {
		return r.journalno;
	}
}
